/**
 * Audio Volume Conversion Utilities
 *
 * Converts between percentage (0-100%) and decibels (dB) for background music volume control.
 *
 * Decibel Scale:
 * - 0 dB = 100% (no reduction)
 * - -6 dB ≈ 50% (half volume)
 * - -20 dB ≈ 32% (default comfortable level)
 * - -60 dB or lower = effectively silent
 *
 * Conversion formulas:
 * - dB to percent: percent = 10^(dB / 20) * 100
 * - Percent to dB: dB = 20 * log10(percent / 100)
 */

/**
 * Convert decibels to percentage (0-100)
 *
 * @param db - Volume in decibels (typically -60 to 0)
 * @returns Volume as percentage (0-100)
 *
 * @example
 * dbToPercent(0)    // 100%
 * dbToPercent(-6)   // ~50%
 * dbToPercent(-20)  // ~10%
 * dbToPercent(-60)  // ~0.1%
 */
export function dbToPercent(db: number): number {
  if (db <= -60) {
    return 0; // Treat very low dB as silent
  }

  // Formula: percent = 10^(dB / 20) * 100
  const percent = Math.pow(10, db / 20) * 100;

  // Round to 1 decimal place and clamp between 0-100
  return Math.max(0, Math.min(100, Math.round(percent * 10) / 10));
}

/**
 * Convert percentage (0-100) to decibels
 *
 * @param percent - Volume as percentage (0-100)
 * @returns Volume in decibels (typically -60 to 0)
 *
 * @example
 * percentToDb(100)  // 0 dB
 * percentToDb(50)   // ~-6 dB
 * percentToDb(10)   // ~-20 dB
 * percentToDb(0)    // -60 dB (silent)
 */
export function percentToDb(percent: number): number {
  // Clamp between 0-100
  percent = Math.max(0, Math.min(100, percent));

  if (percent === 0) {
    return -60; // Treat 0% as -60dB (effectively silent)
  }

  // Formula: dB = 20 * log10(percent / 100)
  const db = 20 * Math.log10(percent / 100);

  // Round to 1 decimal place
  return Math.round(db * 10) / 10;
}

/**
 * Get a human-readable label for the volume percentage
 *
 * @param percent - Volume as percentage (0-100)
 * @returns Human-readable label
 */
export function getVolumeLabel(percent: number): string {
  if (percent === 0) return 'Silent';
  if (percent < 20) return 'Very Low';
  if (percent < 40) return 'Low';
  if (percent < 60) return 'Medium';
  if (percent < 80) return 'High';
  return 'Very High';
}

/**
 * Get suggested volume presets for common use cases
 */
export const VOLUME_PRESETS = {
  silent: { percent: 0, db: -60, label: 'Silent' },
  veryLow: { percent: 10, db: -20, label: 'Very Low (Default)' },
  low: { percent: 25, db: -12, label: 'Low' },
  medium: { percent: 50, db: -6, label: 'Medium' },
  high: { percent: 75, db: -2.5, label: 'High' },
  max: { percent: 100, db: 0, label: 'Maximum' }
} as const;

/**
 * Default background music volume in dB
 * Corresponds to approximately 10% volume which is comfortable for meditation
 */
export const DEFAULT_BACKGROUND_VOLUME_DB = -20;

/**
 * Default background music volume as percentage
 */
export const DEFAULT_BACKGROUND_VOLUME_PERCENT = dbToPercent(DEFAULT_BACKGROUND_VOLUME_DB);

/**
 * Format volume for display with dB and percentage
 *
 * @param volumeDb - Volume in decibels
 * @returns Formatted string like "50% (-6.0 dB)"
 */
export function formatVolumeDisplay(volumeDb: number): string {
  const percent = dbToPercent(volumeDb);
  return `${percent}% (${volumeDb.toFixed(1)} dB)`;
}
